/**
 * LocalStorage management service
 * Provides consistent access to stored data
 */

export interface UserInfo {
  fullName: string;
  username: string;
  id: string;
}

const STORAGE_KEYS = {
  AUTH_TOKEN: "authToken",
  USER_INFO: "userInfo",
  CURRENT_DOCUMENT_ID: "currentDocumentId",
} as const;

/**
 * Get authentication token from localStorage
 */
export function getAuthToken(): string | null {
  return localStorage.getItem(STORAGE_KEYS.AUTH_TOKEN);
}

/**
 * Set authentication token in localStorage
 */
export function setAuthToken(token: string): void {
  localStorage.setItem(STORAGE_KEYS.AUTH_TOKEN, token);
}

/**
 * Get user info from localStorage
 */
export function getUserInfo(): UserInfo | null {
  const userInfoStr = localStorage.getItem(STORAGE_KEYS.USER_INFO);
  if (!userInfoStr) return null;

  try {
    return JSON.parse(userInfoStr) as UserInfo;
  } catch (error) {
    console.error("Failed to parse user info:", error);
    return null;
  }
}

/**
 * Set user info in localStorage
 */
export function setUserInfo(userInfo: UserInfo): void {
  localStorage.setItem(STORAGE_KEYS.USER_INFO, JSON.stringify(userInfo));
}

/**
 * Get current document ID from localStorage
 */
export function getCurrentDocumentId(): string | null {
  return localStorage.getItem(STORAGE_KEYS.CURRENT_DOCUMENT_ID);
}

/**
 * Set current document ID in localStorage
 */
export function setCurrentDocumentId(documentId: string): void {
  localStorage.setItem(STORAGE_KEYS.CURRENT_DOCUMENT_ID, documentId);
}

/**
 * Clear all authentication data from localStorage
 */
export function clearAuthData(): void {
  localStorage.removeItem(STORAGE_KEYS.AUTH_TOKEN);
  localStorage.removeItem(STORAGE_KEYS.USER_INFO);
}

/**
 * Clear all data from localStorage
 */
export function clearAllData(): void {
  localStorage.removeItem(STORAGE_KEYS.AUTH_TOKEN);
  localStorage.removeItem(STORAGE_KEYS.USER_INFO);
  localStorage.removeItem(STORAGE_KEYS.CURRENT_DOCUMENT_ID);
}

/**
 * Clear document specific data from localStorage
 */
export function clearDocumentData(): void {
  localStorage.removeItem(STORAGE_KEYS.CURRENT_DOCUMENT_ID);
  localStorage.removeItem("documentReference");
  localStorage.removeItem("qrCodeUrl");
  localStorage.removeItem("verificationCode");
  localStorage.removeItem("documentFinalized");
}

/**
 * Check if user is authenticated
 */
export function isAuthenticated(): boolean {
  return getAuthToken() !== null && getUserInfo() !== null;
}

/**
 * Set document reference
 */
export function setDocumentReference(reference: string): void {
  localStorage.setItem("documentReference", reference);
}

/**
 * Get document reference
 */
export function getDocumentReference(): string | null {
  return localStorage.getItem("documentReference");
}

/**
 * Set QR code URL
 */
export function setQRCodeUrl(url: string): void {
  localStorage.setItem("qrCodeUrl", url);
}

/**
 * Get QR code URL
 */
export function getQRCodeUrl(): string | null {
  return localStorage.getItem("qrCodeUrl");
}

/**
 * Set verification code
 */
export function setVerificationCode(code: string): void {
  localStorage.setItem("verificationCode", code);
}

/**
 * Get verification code
 */
export function getVerificationCode(): string | null {
  return localStorage.getItem("verificationCode");
}

/**
 * Set document finalized state
 */
export function setDocumentFinalized(finalized: boolean): void {
  localStorage.setItem("documentFinalized", finalized.toString());
}

/**
 * Get document finalized state
 */
export function getDocumentFinalized(): boolean {
  const finalized = localStorage.getItem("documentFinalized");
  return finalized === "true";
}
